import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ProjectsService } from 'app/modules/projects/projects.service';
import { LegacyTasksTabService } from './services/tasks.service';
import { Project } from 'app/modules/projects/projects.types';
import { AccountService } from 'app/core';
import { TaskList, Task, TaskListResult } from './tasks.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { defaultTaskTableFields } from './list/list.constants';
import { Store } from '@ngxs/store';
import { TaskActions } from '@tasks/state/actions';
import { AccountState } from '@state/account/state';

export class BaseTaskModuleResolver {
  constructor(
    protected readonly _tasksService: LegacyTasksTabService,
    protected readonly store: Store,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void {
    if (!route.data.taskModuleRelatedTo) {
      throw new Error('[TaskModule configuration error] - Route data taskModuleRelatedTo is required');
    }
    const relatedToId = this.getRelatedToId(route);

    // TODO: REMOVE
    if (/\/objects\//g.test(state.url)) {
      // TODO MIGRATE TO STATE
      const account = this.store.selectSnapshot(AccountState.getAccount);
      const customObjectId = route.paramMap.get('customObjectId');
      const customObjectValueId = route.paramMap.get('customObjectValueId');
      this._tasksService.baseModuleId = customObjectId;
      this._tasksService.moduleId = customObjectValueId.toString();
      this._tasksService.accountId = account.id;
      this._tasksService.moduleBaseUrl = `accounts/${account.id}/custom_objects/${customObjectId}/values`;
      this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId));
    } else {
      this._tasksService.moduleBaseUrl = undefined; // TODO: REMOVE WHEN MIGRATE CO URL
      this.store.dispatch(new TaskActions.SetRelatedTo(route.data.taskModuleRelatedTo, +relatedToId));
    }
  }

  private getRelatedToId(route: ActivatedRouteSnapshot): number {
    if (route.data.taskModuleRelatedTo === RelatedToType.Project) {
      return route.params.project_id;
    }

    if (route.data.taskModuleRelatedTo === RelatedToType.Account) {
      this._tasksService.isGlobalTasks = true;
      return +this.store.selectSnapshot(AccountState.getAccount)?.id;
    }
    return +route.params.id;
  }
}

@Injectable({
  providedIn: 'root',
})
export class TaskTabListsResolver extends BaseTaskModuleResolver {
  constructor(
    protected readonly tasksService: LegacyTasksTabService,
    protected readonly store: Store,
  ) {
    super(tasksService, store);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<TaskListResult> {
    super.resolve(route, state);
    this.store.dispatch(new TaskActions.GetTaskLists());
    return this._tasksService.getTaskLists();
  }
}

@Injectable({
  providedIn: 'root',
})
export class TaskTabListResolver extends BaseTaskModuleResolver {
  constructor(
    protected readonly router: Router,
    protected readonly tasksService: LegacyTasksTabService,
    protected readonly store: Store,
  ) {
    super(tasksService, store);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<TaskList> {
    super.resolve(route, state);

    const includeTasks = route.data.taskModuleRelatedTo === RelatedToType.Project;
    return this.tasksService.getTaskListById(route.paramMap.get('task_list_id'), null, includeTasks).pipe(
      // Error here means the requested contact is not available
      catchError((error) => {
        // Log the error
        console.error(error);

        // Get the parent url
        const parentUrl = state.url.split('/').slice(0, -1).join('/');

        // Navigate to there
        this.router.navigateByUrl(parentUrl);

        // Throw an error
        return throwError(error);
      }),
    );
  }
}

/**
 * @deprecated This class will be removed in future versions.
 * Please use dispatch(new TaskActions.GetTaskDetail(task_list_id, task_id)) instead.
 */
@Injectable({
  providedIn: 'root',
})
export class TaskTabResolver extends BaseTaskModuleResolver {
  constructor(
    protected readonly router: Router,
    protected readonly tasksService: LegacyTasksTabService,
    protected readonly store: Store,
  ) {
    super(tasksService, store);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Task> {
    super.resolve(route, state);
    return this._tasksService.getTaskById(route.paramMap.get('task_id'), null).pipe(
      // Error here means the requested task is not available
      catchError((error) => {
        // Log the error
        console.error(error);

        // Get the parent url
        const parentUrl = state.url.split('/').slice(0, -1).join('/');

        // Navigate to there
        this.router.navigateByUrl(parentUrl);

        // Throw an error
        return throwError(error);
      }),
    );
  }
}

@Injectable({ providedIn: 'root' })
export class TaskFieldDefsResolver {
  constructor(private readonly accountService: AccountService) {}

  resolve(): Observable<Partial<FieldDef>[]> {
    return this.accountService.getFieldDefs().pipe(
      map((fields: FieldDef[]) => {
        const taskRelatedFields = fields.filter(
          ({ related_to_type }: FieldDef) => related_to_type === RelatedToType.Task,
        );

        const defaultTaskFields = defaultTaskTableFields.filter(
          (defaultField) => !taskRelatedFields.map((dynamicField) => dynamicField.name).includes(defaultField.name),
        );
        return [...defaultTaskFields, ...taskRelatedFields];
      }),
    );
  }
}
